import AppRoute from 'src/router/app-route';
import { useAuthStore } from 'stores/auth-store';
import { useJobSearchStore } from 'stores/job-search-store';
import { useJobShortlistStore } from 'stores/job-shortlist-store';

export const Route = Object.freeze({
    AccountSecurityPage: 'AccountSecurityPage',
    AccountSettingsPage: 'AccountSettingsPage',
    AccountSetupPage: 'AccountSetupPage',
    CompanyManagerPage: 'CompanyManagerPage',
    ContactPage: 'ContactPage',
    EmployerLandingPage: 'EmployerLandingPage',
    JobApplicationPage: 'JobApplicationPage',
    JobApplicationReviewPage: 'JobApplicationReviewPage',
    JobApplicationWizardPage: 'JobApplicationWizardPage',
    JobManagerPage: 'JobManagerPage',
    JobManagerAnalyticsPage: 'JobManagerAnalyticsPage',
    JobManagerApplicationPage: 'JobManagerApplicationPage',
    JobManagerApplicationsListPage: 'JobManagerApplicationsListPage',
    JobManagerListPage: 'JobManagerListPage',
    JobManagerListingPage: 'JobManagerListingPage',
    JobManagerPreviewPage: 'JobManagerPreviewPage',
    JobManagerWizardPage: 'JobManagerWizardPage',
    JobPage: 'JobPage',
    JobResultPage: 'JobResultPage',
    LandingPage: 'LandingPage',
    LoginPage: 'LoginPage',
    PricingPage: 'PricingPage',
    PrivacyPolicyPage: 'PrivacyPolicyPage',
    ProfileManagerPage: 'ProfileManagerPage',
    SearchResultsPage: 'SearchResultsPage',
    ShortlistPage: 'ShortlistPage',
    ShortlistResultPage: 'ShortlistResultPage',
    TermsOfServicePage: 'TermsOfServicePage',
    // Error pages
    PageNotFoundErrorPage: 'PageNotFoundErrorPage',
    UnknownErrorPage: 'UnknownErrorPage'
});

const routes = [
    {
        path: '/login',
        component: () => import('layouts/EmptyLayout.vue'),
        children: [
            {
                path: '',
                name: Route.LoginPage,
                meta: {
                    public: true
                },
                beforeEnter: (to, from, next) => {
                    if (useAuthStore().isUserAuthenticated) {
                        return next(AppRoute.build(Route.LandingPage));
                    }
                    next();
                },
                component: () => import('pages/LoginPage.vue')
            }
        ]
    },
    {
        path: '',
        component: () => import('layouts/LandingLayout.vue'),
        children: [
            {
                path: '',
                name: Route.LandingPage,
                meta: {
                    public: true
                },
                component: () => import('pages/LandingPage.vue')
            },
            {
                path: '/hire',
                name: Route.EmployerLandingPage,
                meta: {
                    public: true
                },
                component: () => import('pages/EmployerLandingPage.vue')
            }
        ]
    },
    {
        path: '',
        component: () => import('layouts/SearchLayout.vue'),
        children: [
            {
                path: '/search',
                name: Route.SearchResultsPage,
                meta: {
                    public: true
                },
                component: () => import('pages/SearchResultsPage.vue'),
                children: [
                    {
                        path: '/shortlist',
                        name: Route.ShortlistPage,
                        meta: {
                            public: false
                        },
                        component: () => import('pages/ShortlistPage.vue'),
                        children: [
                            {
                                name: Route.ShortlistResultPage,
                                path: '/shortlist/job/:jobScopeId',
                                meta: {
                                    retainScrollPosition: true
                                },
                                beforeEnter: (to, from, next) => {
                                    if (!useJobShortlistStore().isLoaded) {
                                        const { jobScopeId } = to.params;
                                        return next(AppRoute.build(Route.JobPage, { jobScopeId }));
                                    }
                                    next();
                                },
                                component: () => import('pages/JobResultPage.vue')
                            }
                        ]
                    },
                    {
                        path: '/search/job/:jobScopeId',
                        name: Route.JobResultPage,
                        meta: {
                            public: true,
                            retainScrollPosition: true
                        },
                        beforeEnter: (to, from, next) => {
                            const jobSearchStore = useJobSearchStore();
                            if (!jobSearchStore.isSearchLoaded) {
                                const { jobScopeId } = to.params;
                                return next(AppRoute.build(Route.JobPage, { jobScopeId }));
                            }
                            next();
                        },
                        component: () => import('pages/JobResultPage.vue')
                    }
                ]
            },
            {
                path: '/job/:jobScopeId',
                name: Route.JobPage,
                meta: { public: true },
                component: () => import('pages/JobPage.vue')
            }
        ]
    },
    {
        path: '',
        component: () => import('layouts/MainLayout.vue'),
        children: [
            {
                path: '/manage/companies',
                name: Route.CompanyManagerPage,
                component: () => import('pages/CompanyManagerPage.vue')
            },
            {
                path: '/job/:jobScopeId/application/:step?',
                name: Route.JobApplicationWizardPage,
                component: () => import('pages/JobApplicationWizardPage.vue'),
                beforeEnter: (to, from, next) => {
                    if (!to.params.step || isNaN(parseInt(to.params.step, 10))) {
                        next(AppRoute.build(Route.JobApplicationWizardPage, { ...to.params, step: 1 }));
                        return;
                    }
                    next();
                }
            },
            {
                path: '/manage/applications',
                name: Route.JobApplicationPage,
                component: () => import('pages/JobApplicationPage.vue')
            },
            {
                path: '/manage/applications/:jobApplicationId',
                name: Route.JobApplicationReviewPage,
                component: () => import('pages/JobApplicationReviewPage.vue')
            },
            {
                path: '/manage/jobs',
                name: Route.JobManagerListPage,
                component: () => import('pages/JobManagerListPage.vue'),
                beforeEnter: (to, from, next) => {
                    const authStore = useAuthStore();
                    if (authStore.user?.totalJobs === 0) {
                        return next(AppRoute.build(Route.LandingPage));
                    }
                    next();
                }
            },
            {
                path: '/manage/jobs/:jobId',
                name: Route.JobManagerPage,
                component: () => import('pages/JobManagerPage.vue'),
                beforeEnter: (to, from, next) => {
                    if (to.name === Route.JobManagerPage) {
                        next(AppRoute.build(Route.JobManagerListingPage, { jobId: to.params.jobId }));
                        return;
                    }
                    next();
                },
                children: [
                    {
                        path: '',
                        name: Route.JobManagerListingPage,
                        component: () => import('pages/JobManagerListingPage.vue')
                    }, {
                        path: 'analytics',
                        name: Route.JobManagerAnalyticsPage,
                        component: () => import('pages/JobManagerAnalyticsPage.vue')
                    }, {
                        path: 'applications',
                        name: Route.JobManagerApplicationsListPage,
                        component: () => import('pages/JobManagerApplicationsListPage.vue'),
                        children: [
                            {
                                path: ':applicationId',
                                name: Route.JobManagerApplicationPage,
                                meta: {
                                    retainScrollPosition: true
                                },
                                component: () => import('pages/JobManagerApplicationPage.vue')
                            }
                        ]
                    }
                ]
            },
            {
                path: '/manage/jobs/create',
                name: Route.JobManagerWizardPage,
                component: () => import('pages/JobManagerWizardPage.vue')
            },
            {
                path: '/account/settings',
                name: Route.AccountSettingsPage,
                component: () => import('pages/AccountSettingsPage.vue'),
                meta: {
                    bypassSetup: true
                }
            },
            {
                path: '/account/security',
                name: Route.AccountSecurityPage,
                component: () => import('pages/AccountSecurityPage.vue'),
                meta: {
                    bypassSetup: true
                }
            },
            {
                path: '/account/profile',
                name: Route.ProfileManagerPage,
                component: () => import('pages/ProfileManagerPage.vue'),
                meta: {
                    requiresAuth: true,
                    bypassSetup: true
                }
            },
            {
                name: Route.AccountSetupPage,
                path: '/account/setup',
                component: () => import('components/welcome-manager/WelcomeManagerDialog.vue'),
                beforeEnter: (to, from, next) => {
                    const authStore = useAuthStore();
                    if (authStore.user?.isReady) {
                        return next(AppRoute.build(Route.ManageUserProfile));
                    }
                    return next();
                }
            },
            {
                path: '/contact',
                name: Route.ContactPage,
                component: () => import('pages/ContactPage.vue'),
                meta: {
                    public: true,
                    bypassSetup: true
                }
            },
            {
                path: '/hire/pricing',
                name: Route.PricingPage,
                component: () => import('pages/PricingPage.vue'),
                meta: {
                    public: true,
                }
            },
            {
                path: '/legal/privacy',
                name: Route.PrivacyPolicyPage,
                component: () => import('pages/PrivacyPolicyPage.vue'),
                meta: {
                    public: true,
                    bypassSetup: true
                }
            },
            {
                path: '/legal/terms',
                name: Route.TermsOfServicePage,
                component: () => import('pages/TermsOfServicePage.vue'),
                meta: {
                    public: true,
                    bypassSetup: true
                }
            }
        ]
    },
    {
        component: () => import('layouts/SearchLayout.vue'),
        path: '/error',
        meta: {
            public: true
        },
        children: [
            {
                name: Route.PageNotFoundErrorPage,
                path: '/page-not-found',
                component: () => import('pages/error-pages/PageNotFoundErrorPage.vue'),
                meta: {
                    bypassSetup: true
                }
            },
            {
                name: Route.UnknownErrorPage,
                path: '/unknown-error',
                component: () => import('pages/error-pages/UnknownErrorPage.vue'),
                meta: {
                    bypassSetup: true
                }
            }
        ]
    },
    {
        path: '/:catchAll(.*)*',
        beforeEnter: (to, from, next) => {
            next(AppRoute.build(Route.PageNotFound));
        }
    }
];

export default routes;
